import { Controller } from "@hotwired/stimulus"
import ComposerFilePicker from "../utils/composer_file_picker"
import { followBottom } from "../utils/chat_scroll"
import { createConsumer } from "@rails/actioncable"
import { escapeMd } from "../utils/inline_markdown"

// Community Chat — the Jane watercooler(s). Subscribes to CommunityChannel
// for the active room (the general room, or one organization's private
// watercooler — set via the element's data-community-chat-organization-id),
// appends each broadcast line, and sends new lines via
// perform("speak", {body}). This IS a bottom-anchored chat (unlike the feed),
// so it scrolls to the newest line on load and whenever a line arrives.
//
// The composer supports @-mentions: typing "@que" opens a typeahead
// (GET /apps/community/mention_candidates, or the /org/:id variant for an
// org room) and picking someone inserts the shared @[Name](user:ID) token the
// server reconciles into CommunityMessageMentions and renders as a /u/:id
// chip. The token format and escaping are the same ones tasks use
// (utils/inline_markdown), so nothing is duplicated.
//
// Graceful degradation: if the cable can't connect the composer still shows a
// "Chat is reconnecting…" note rather than a broken page, and every cable call
// is wrapped defensively.
//
// Attachments (FILES_FEATURE_SPEC.md): a cable frame can't carry file bytes,
// so a chosen file is staged locally (same chip-list idea as
// message_attach_controller.js, but hand-rolled here rather than reused —
// this composer's submit is a fetch-then-cable-perform, not a native form
// post, so the staging/upload split is genuinely different) and only
// actually uploaded — via CommunityAttachmentsController, over ordinary
// fetch/FormData — at send time. The resulting blob signed ids ride the
// `speak` cable frame; CommunityChannel re-verifies ownership before
// attaching them (see that channel).
//
// "Choose from Files" library picks (openFilePicker/searchFiles/
// pickLibraryFile) mirror message_attach_controller.js's version of the same
// idea, over the SAME GET /user_files + GET /apps/files/search endpoints —
// but a pick here stages only an id (kind: "library"), never bytes, and that
// id rides as `user_file_ids[]` on the SAME #uploadAttachments fetch a fresh
// upload uses (CommunityAttachmentsController resolves both kinds through
// MessageAttachments, same as every other composer). The resulting blob
// signed id is indistinguishable from an upload's by the time it reaches
// `speak` — CommunityChannel#speak re-verifies ownership either way.
export default class extends Controller {
    static targets = ["messages", "input", "status", "fileInput", "attachmentsRow", "attachPanel", "attachSearchInput", "attachResults"]
    static values = { attachmentsUrl: String, filesIndexUrl: String, filesSearchUrl: String }

    connect() {
        this._mention = null
        this._mentionDebounce = null
        this._mentionMenu = null
        this._organizationId = this.element.dataset.communityChatOrganizationId || null
        // Which world this page is in (the Square, or San Francisco): the
        // cable puts us there, and only lines said in it get a speech bubble.
        this._world = this.element.dataset.communityChatWorld || "square"
        this._attachments = []
        this._attachSeq = 0
        this._filePicker = new ComposerFilePicker(this)
        // The Square (general room only): the map controller has no socket of
        // its own, so this one relays cable frames to it and sends its frames
        // for it, and remembers where it last said we stood so a line typed
        // here is said at that spot. See community_square_controller.js.
        this._squarePosition = null
        // People blocked either way on the map: their new lines are skipped
        // here too (the server already left their old ones out of the log).
        this._blocked = new Set(this.#initialBlocked())
        this._onSquareBlocked = (e) => { this._blocked = new Set(e.detail?.ids || []) }
        window.addEventListener("community-square:blocked", this._onSquareBlocked)
        this._onSquareSend = (e) => this.#squareSend(e.detail)
        this._onSquarePosition = (e) => { this._squarePosition = e.detail }
        window.addEventListener("community-square:send", this._onSquareSend)
        window.addEventListener("community-square:position", this._onSquarePosition)
        this.#scrollToBottom()
        this.#connectCable()
    }

    disconnect() {
        this._filePicker.disconnect()
        window.removeEventListener("community-square:send", this._onSquareSend)
        window.removeEventListener("community-square:blocked", this._onSquareBlocked)
        window.removeEventListener("community-square:position", this._onSquarePosition)
        this._closeMention()
        if (this.subscription) { try { this.subscription.unsubscribe() } catch (e) { /* noop */ } }
        if (this.consumer) { try { this.consumer.disconnect() } catch (e) { /* noop */ } }
    }

    // --- ActionCable --------------------------------------------------------

    #connectCable() {
        if (typeof createConsumer !== "function") { this.#degrade(); return }
        try {
            this.consumer = createConsumer()
            this.subscription = this.consumer.subscriptions.create(
                { channel: "CommunityChannel", organization_id: this._organizationId, world: this._world },
                {
                    connected: () => this.#setStatus(""),
                    disconnected: () => this.#setStatus("Chat is reconnecting…"),
                    rejected: () => this.#setStatus("Please sign in to join the chat."),
                    received: (data) => this.#onReceived(data)
                }
            )
        } catch (e) {
            this.#degrade()
        }
    }

    #onReceived(data) {
        if (!data || !data.type) return
        if (data.type === "message") {
            if (data.author && this._blocked.has(data.author)) return
            this.#appendMessage(data.html)
            if (data.said && (data.said.world || "square") === this._world) this.#squareFrame({ type: "said", said: data.said })
            return
        }
        if (data.type === "reaction") { this.#applyReaction(data); return }
        if (["roster", "join", "leave", "move", "hello", "voice", "signal"].includes(data.type)) this.#squareFrame(data)
    }

    #initialBlocked() {
        try {
            const square = document.querySelector("[data-controller~='community-square']")
            const city = document.querySelector("[data-controller~='community-city']")
            const config = square?.dataset.communitySquareConfigValue || city?.dataset.communityCityConfigValue || "{}"
            return JSON.parse(config).blocked || []
        } catch (e) {
            return []
        }
    }

    #squareFrame(data) {
        window.dispatchEvent(new CustomEvent("community-square:frame", { detail: data }))
    }

    #squareSend(detail) {
        if (!this.subscription || !detail || !["move", "ping", "voice", "signal"].includes(detail.action)) return
        try { this.subscription.perform(detail.action, detail.payload || {}) } catch (e) { /* the next frame retries */ }
    }

    // --- Composer -----------------------------------------------------------

    async speak(event) {
        event.preventDefault()
        if (!this.hasInputTarget) return
        const body = this.inputTarget.value.trim()
        if (!body && this._attachments.length === 0) return
        if (!this.subscription) { this.#setStatus("Chat is offline right now."); return }

        let blobSignedIds = []
        if (this._attachments.length > 0) {
            const uploaded = await this.#uploadAttachments()
            if (uploaded === null) return // error already surfaced via #setStatus
            blobSignedIds = uploaded
        }

        try {
            const spot = this._squarePosition || {}
            this.subscription.perform("speak", { body, attachment_blob_signed_ids: blobSignedIds, x: spot.x, y: spot.y })
            this.inputTarget.value = ""
            this.#clearAttachments()
            this._closeMention()
        } catch (e) {
            this.#setStatus("Message not sent - reconnecting.")
        }
    }

    // --- Attachments ----------------------------------------------------------
    // Staged locally as plain File objects; only actually uploaded (via
    // CommunityAttachmentsController) once the user hits send. Nothing is
    // uploaded — and nothing counts against quota — for a file picked and
    // then removed before sending.

    pickFile() {
        if (this.hasFileInputTarget) this.fileInputTarget.click()
    }

    filesChosen(e) {
        Array.from(e.target.files || []).forEach((file) => {
            this._attachments.push({ localId: `up-${++this._attachSeq}`, kind: "upload", file, name: file.name })
        })
        if (this.hasFileInputTarget) this.fileInputTarget.value = ""
        this.#renderAttachments()
    }

    openFilePicker() {
        this._filePicker.openFilePicker()
    }

    closeAttachPanel() {
        this._filePicker.closeAttachPanel()
    }

    searchFiles() {
        this._filePicker.searchFiles()
    }

    attachSearchKeydown(event) {
        this._filePicker.attachSearchKeydown(event)
    }

    // A library pick carries only an id — resolved against
    // current_user.user_files (never a bare id lookup) by
    // CommunityAttachmentsController at send time, and re-verified again by
    // CommunityChannel#speak against the blob signed id that resolves to
    // (see that channel's comment) — so a stale or another member's id just
    // silently attaches nothing, twice over.
    pickLibraryFile(e) {
        const id = e.currentTarget.dataset.id
        const name = e.currentTarget.dataset.name
        if (!id) return
        if (this._attachments.some((a) => a.kind === "library" && a.id === id)) {
            this.closeAttachPanel()
            return
        }
        this._attachments.push({ localId: `lib-${id}`, kind: "library", id, name })
        this.closeAttachPanel()
        this.#renderAttachments()
    }

    removeAttachment(e) {
        const localId = e.currentTarget.dataset.localId
        this._attachments = this._attachments.filter((a) => a.localId !== localId)
        this.#renderAttachments()
    }

    // Resolves every staged attachment — fresh uploads AND library picks —
    // through CommunityAttachmentsController (Files::Upload quota/checksum
    // dedup for uploads, current_user.user_files-scoped lookup for library
    // ids, same MessageAttachments concern every other composer's endpoint
    // uses) and returns the blob signed ids to send with `speak`, or null on
    // any failure (already surfaced via #setStatus, and nothing staged is
    // cleared so the user can retry).
    async #uploadAttachments() {
        const formData = new FormData()
        this._attachments.forEach((a) => {
            if (a.kind === "library") {
                formData.append("user_file_ids[]", a.id)
            } else {
                formData.append("attachments[]", a.file)
            }
        })

        try {
            const response = await fetch(this.attachmentsUrlValue, {
                method: "POST",
                headers: { Accept: "application/json", "X-CSRF-Token": this.#csrfToken() },
                body: formData,
                credentials: "same-origin"
            })
            const data = await response.json().catch(() => null)
            if (!response.ok || !data || !data.ok) {
                this.#setStatus((data && data.error) || "Could not attach those files.")
                return null
            }
            return data.attachments.map((a) => a.blob_signed_id)
        } catch (e) {
            this.#setStatus("Could not attach those files.")
            return null
        }
    }

    #clearAttachments() {
        this._attachments = []
        this.#renderAttachments()
    }

    #renderAttachments() {
        if (!this.hasAttachmentsRowTarget) return
        const row = this.attachmentsRowTarget
        row.innerHTML = ""
        row.hidden = this._attachments.length === 0
        this._attachments.forEach((a) => {
            const chip = document.createElement("span")
            chip.className = "composer-attachment-chip"
            const icon = document.createElement("i")
            icon.className = a.kind === "library" ? "bi bi-folder2" : "bi bi-paperclip"
            icon.setAttribute("aria-hidden", "true")
            const label = document.createElement("span")
            label.className = "composer-attachment-chip-name"
            label.textContent = a.name
            const remove = document.createElement("button")
            remove.type = "button"
            remove.className = "composer-attachment-chip-remove"
            remove.setAttribute("aria-label", `Remove ${a.name}`)
            remove.dataset.localId = a.localId
            remove.dataset.action = "click->community-chat#removeAttachment"
            remove.innerHTML = '<i class="bi bi-x" aria-hidden="true"></i>'
            chip.appendChild(icon)
            chip.appendChild(label)
            chip.appendChild(remove)
            row.appendChild(chip)
        })
    }

    #csrfToken() {
        return document.querySelector("meta[name='csrf-token']")?.content || ""
    }

    #appendMessage(html) {
        if (!this.hasMessagesTarget || !html) return
        // Clear the empty-state placeholder the first time a line arrives.
        const empty = this.messagesTarget.querySelector(".cc-empty")
        if (empty) empty.remove()
        this.messagesTarget.insertAdjacentHTML("beforeend", html)
        this.#scrollToBottom()
    }

    // Bound on this controller's own element (.cc-chat, see
    // community/index.html.slim's data-action) for "emoji-picker:picked",
    // dispatched by the shared reaction picker (community/_reactions renders
    // it in event-only mode — no react_url, so it never POSTs and only this
    // cable send happens). The picker's own message id comes from its .rxp
    // root's data-message-id (community/_reactions's `data:` local); event.target
    // is inside that picker, so the closest lookup finds it regardless of
    // which part of the picker was clicked.
    reactPicked(event) {
        const emoji = event.detail && event.detail.emoji
        const host = event.target.closest("[data-message-id]")
        const messageId = host && host.dataset.messageId
        if (!emoji || !messageId) return
        if (!this.subscription) { this.#setStatus("Reactions are offline right now."); return }
        try {
            this.subscription.perform("react", { message_id: messageId, emoji })
        } catch (e) {
            this.#setStatus("Reaction not sent - reconnecting.")
        }
    }

    #applyReaction(data) {
        if (!this.hasMessagesTarget || !data.html || !data.id) return
        const row = this.messagesTarget.querySelector(`#cc_msg_${data.id}_reactions`)
        if (row) row.outerHTML = data.html
    }

    #scrollToBottom() {
        if (this.hasMessagesTarget) followBottom(this, this.messagesTarget).contentChanged()
    }

    #setStatus(text) {
        if (!this.hasStatusTarget) return
        this.statusTarget.textContent = text || ""
        this.statusTarget.hidden = !text
    }

    #degrade() {
        this.#setStatus("Chat is reconnecting…")
    }

    // --- Mention autocomplete (@) -------------------------------------------
    //
    // Detects an "@query" fragment before the caret in the plain-text composer,
    // fetches candidates, and on pick splices the shared @[Name](user:ID) token
    // into the input value. Bound from the view: input -> onInput, keydown ->
    // onKeydown, blur -> onBlur.

    onInput() {
        if (!this.hasInputTarget) return
        this._detectMention()
    }

    onKeydown(event) {
        // While the dropdown is open it owns Arrow/Enter/Escape/Tab so the
        // keystroke picks a mention instead of submitting/moving the caret.
        if (!this._mention) return
        if (event.key === "Escape") { event.preventDefault(); this._closeMention(); return }
        const items = this._mention.items || []
        if (event.key === "ArrowDown") { event.preventDefault(); this._moveMention(1); return }
        if (event.key === "ArrowUp") { event.preventDefault(); this._moveMention(-1); return }
        if ((event.key === "Enter" || event.key === "Tab") && items.length && this._mention.index >= 0) {
            event.preventDefault()
            this._pickMention(this._mention.index)
        }
    }

    onBlur() {
        // Delay so a mousedown on a menu row wins over the blur that dismisses.
        setTimeout(() => this._closeMention(), 120)
    }

    _detectMention() {
        const input = this.inputTarget
        const caret = input.selectionStart
        if (caret === null || caret === undefined) return this._closeMention()
        const before = input.value.slice(0, caret)
        // "@query" at the caret: preceded by start-of-string or whitespace, and
        // the query has no spaces or a second "@".
        const match = before.match(/(?:^|\s)@([^\s@]*)$/)
        if (!match) return this._closeMention()

        const query = match[1]
        this._mention = {
            query,
            start: caret - query.length - 1, // index of the "@"
            end: caret,
            items: (this._mention && this._mention.query === query) ? this._mention.items : [],
            index: (this._mention && this._mention.query === query) ? this._mention.index : -1
        }
        this._ensureMentionMenu()
        this._positionMentionMenu()
        this._renderMentionItems()
        this._scheduleMentionFetch(query)
    }

    _scheduleMentionFetch(query) {
        if (this._mentionDebounce) clearTimeout(this._mentionDebounce)
        this._mentionDebounce = setTimeout(() => this._fetchMentions(query), 180)
    }

    async _fetchMentions(query) {
        try {
            const base = this._organizationId
                ? `/apps/community/org/${this._organizationId}/mention_candidates`
                : "/apps/community/mention_candidates"
            const res = await fetch(`${base}?q=${encodeURIComponent(query)}`, {
                headers: { Accept: "application/json" }
            })
            if (!res.ok) throw new Error("mention candidates failed")
            const payload = await res.json()
            if (!this._mention || this._mention.query !== query) return
            this._mention.items = payload.people || []
            this._mention.index = this._mention.items.length ? 0 : -1
            this._renderMentionItems()
        } catch (e) {
            this._closeMention()
        }
    }

    _ensureMentionMenu() {
        if (this._mentionMenu) return
        const menu = document.createElement("div")
        menu.className = "tasks-mention-menu"
        document.body.appendChild(menu)
        this._mentionMenu = menu
    }

    _renderMentionItems() {
        const menu = this._mentionMenu
        if (!menu || !this._mention) return
        const items = this._mention.items || []
        menu.innerHTML = ""
        if (!items.length) { menu.hidden = true; return }
        menu.hidden = false
        items.forEach((person, index) => {
            const row = document.createElement("button")
            row.type = "button"
            row.className = "tasks-mention-item"
            row.classList.toggle("is-active", index === this._mention.index)

            const name = document.createElement("span")
            name.className = "tasks-mention-item-name"
            name.textContent = person.name || ""
            row.appendChild(name)

            if (person.meta) {
                const meta = document.createElement("span")
                meta.className = "tasks-mention-item-meta"
                meta.textContent = person.meta
                row.appendChild(meta)
            }
            row.addEventListener("mousedown", (ev) => { ev.preventDefault(); this._pickMention(index) })
            row.addEventListener("mouseenter", () => this._setMentionIndex(index))
            menu.appendChild(row)
        })
    }

    _positionMentionMenu() {
        const menu = this._mentionMenu
        if (!menu || !this.hasInputTarget) return
        const rect = this.inputTarget.getBoundingClientRect()
        menu.style.left = `${rect.left}px`
        menu.style.width = `${Math.max(rect.width, 220)}px`
        // Open upward: the composer sits at the bottom of the shell.
        menu.style.bottom = `${window.innerHeight - rect.top + 6}px`
        menu.style.top = "auto"
    }

    _moveMention(delta) {
        const items = (this._mention && this._mention.items) || []
        if (!items.length) return
        let next = this._mention.index + delta
        if (next < 0) next = items.length - 1
        if (next >= items.length) next = 0
        this._setMentionIndex(next)
    }

    _setMentionIndex(index) {
        if (!this._mention) return
        this._mention.index = index
        if (!this._mentionMenu) return
        Array.from(this._mentionMenu.children).forEach((row, i) => row.classList.toggle("is-active", i === index))
    }

    _pickMention(index) {
        const m = this._mention
        if (!m) return
        const person = (m.items || [])[index]
        if (!person) { this._closeMention(); return }
        const id = String(person.token || "").replace(/^user:/, "")
        if (!/^\d+$/.test(id)) { this._closeMention(); return }

        const input = this.inputTarget
        const value = input.value
        // The @[Name](user:ID) token, trailing space so the next word is clear.
        const token = `@[${escapeMd(person.name || "")}](user:${id}) `
        input.value = value.slice(0, m.start) + token + value.slice(m.end)
        const pos = m.start + token.length
        input.setSelectionRange(pos, pos)
        input.focus()
        this._closeMention()
    }

    _closeMention() {
        if (this._mentionDebounce) { clearTimeout(this._mentionDebounce); this._mentionDebounce = null }
        this._mention = null
        if (this._mentionMenu) {
            this._mentionMenu.remove()
            this._mentionMenu = null
        }
    }
}
